import { useEffect, useState } from 'react';
import * as Dialog from '@ewf/components/Dialog';
import { useNotificationStore } from '@ewf/stores/notifications';
import { useConnectionStore } from '@ewf/stores/connection';
import { useWalletEnvStore } from '@ewf/stores/wallet-env';
import { useApiServiceStore } from '@ewf/stores/api';
import { TxExecuting } from '@ewf/components/TxExecuting';
import { TxSuccess } from '@ewf/components/TxSuccess';
import { TxFailure } from '@ewf/components/TxFailure';
import { Confirm } from './Confirm';
import { MARKETPLACE_CHAINS } from '@shared/types/enums';
import { useClaimLowerStore } from '@ewf/stores/claim-lowers';
import { Transaction } from '@shared/types/entities/transaction';
import { ConnectLiftingWallet } from '../Lifting/ConnectLiftingWallet/ConnectLiftingWallet';
import { addClaimingLower } from '@ewf/utils/claim-lowers-storage';
import { useWalletStore } from '@ewf/stores/wallets';
import { formatLedgerError } from '@ewf/stores/ledger';

type Step = 'connect-eth' | 'confirm' | 'executing' | 'success' | 'failure';

export const ClaimLower = ({ isOpen, onClose: onCloseDialog, transaction }: Props): JSX.Element => {
  const workerApi = useApiServiceStore.getState().api;
  const [step, setStep] = useState<Step>('connect-eth');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [txUrl, setTxUrl] = useState<string>('');
  const [fee, setFee] = useState<number>(0.0);
  const [isHWWallet, setHWWallet] = useState<boolean>(false);

  const walletConst = useWalletEnvStore((state) => state.walletConst);
  const isConnectedETH = useConnectionStore((state) => state.isConnectedETH);
  const addressETH = useConnectionStore((state) => state.addressETH);
  const addressEWX = useConnectionStore((state) => state.addressEWX);
  const disconnect = useConnectionStore((state) => state.disconnect);
  const claimLower = useClaimLowerStore((state) => state.claimLower);
  const status = useClaimLowerStore((state) => state.status);
  const isLedger = useWalletStore((state) => state.isLedger);

  const createClaimLowersSuccessNotification = useNotificationStore(
    (state) => state.liftingSuccessNotification, // Use lifting notification for now
  );
  const createClaimLowersErrorNotification = useNotificationStore(
    (state) => state.liftingErrorNotification, // Use lifting notification for now
  );

  useEffect(() => {
    if (isOpen) {
      setStep('connect-eth');
    }
  }, [isOpen]);

  const onClose = async () => {
    onCloseDialog();
    setTimeout(async () => {
      setTxUrl('');
      setErrorMsg('');
      setFee(0);
      setHWWallet(false);

      // Disconnect wallet after closing
      if (isConnectedETH) {
        await disconnect(MARKETPLACE_CHAINS.ETH);
      }

      setStep('connect-eth');
    }, 300);
  };

  const connect = (method: 'wc' | 'ledger') => {
    setHWWallet(method === 'ledger');
    setStep('confirm');
  };

  const confirm = async () => {
    setStep('executing');

    try {
      if (isHWWallet) {
        if (isLedger()) {
          const plugged = await workerApi.isDevicePlugged();
          if (!plugged) {
            throw new Error('Please plug in or unlock your Ledger device');
          }

          const locked = await workerApi.isDeviceLocked();
          if (locked) {
            throw new Error('Please unlock ledger device');
          }
        }

        if (!transaction) {
          throw new Error('Transaction not found');
        }

        const { txnHash, errorStatus } = await workerApi.deviceClaimLower(
          transaction.encodedLowerProof!,
          MARKETPLACE_CHAINS.ETH,
        );

        if (errorStatus) {
          console.error(errorStatus);
          const errorMsg = formatLedgerError(errorStatus);
          setErrorMsg(errorMsg);
          setStep('failure');
          createClaimLowersErrorNotification();
          return;
        }

        if (transaction?.lowerId && addressEWX) {
          addClaimingLower(addressEWX, transaction.lowerId);
        }

        setTxUrl(txnHash || '');
        setStep('success');
        createClaimLowersSuccessNotification(0);
      } else {
        if (!transaction) {
          throw new Error('Transaction not found');
        }
        await claimLower(
          addressETH,
          transaction.encodedLowerProof!,
          MARKETPLACE_CHAINS.ETH,
          async ({ status, blockHash, errorMsg }) => {
            if (status === 'success') {
              // Store the lowerId in local storage to track claiming status
              if (transaction?.lowerId && addressEWX) {
                addClaimingLower(addressEWX, transaction.lowerId);
              }

              setTxUrl(blockHash || '');
              setStep('success');
              createClaimLowersSuccessNotification(0);
              return;
            }
            setErrorMsg(errorMsg || 'An unknown error occurred');
            setStep('failure');
            createClaimLowersErrorNotification();
          },
        );
      }
    } catch (error) {
      console.error(error);
      if (error instanceof Error) {
        setErrorMsg(error.message);
      } else if (typeof error === 'object' && error?.['message']) {
        setErrorMsg(error['message']);
      } else {
        setErrorMsg('An unknown error occurred');
      }
      setStep('failure');
      createClaimLowersErrorNotification();
    }
  };

  const confirmGoBack = () => {
    setStep('connect-eth');
  };

  useEffect(() => {
    if (!transaction?.encodedLowerProof) return;
    workerApi
      .getFeeClaimLower(transaction.encodedLowerProof, addressETH)
      .then((fee) => setFee(Number(fee)));
  }, [workerApi, isOpen, transaction?.lowerId]);

  const stepComponents: Record<Step, JSX.Element> = {
    'connect-eth': (
      <ConnectLiftingWallet
        network={MARKETPLACE_CHAINS.ETH}
        onConnect={connect}
        onClose={onClose}
        goBack={confirmGoBack}
      />
    ),
    confirm: (
      <Confirm
        fee={fee}
        amount={transaction?.amount || 0}
        connectedAddress={addressETH}
        recipientAddress={transaction?.addressEWC || ''}
        onConfirm={confirm}
        onGoBack={confirmGoBack}
        onClose={onClose}
      />
    ),
    executing: <TxExecuting operation="claim-lowers" txStatus={status} address={addressETH} />,
    success: (
      <TxSuccess
        operation="claim-lowers"
        txUrl={`${walletConst.ethExplorerUrl}/${txUrl}`}
        title="Claim Lowers Transaction"
        description="Your claim lowers transaction has been submitted successfully."
        onNext={onClose}
        onClose={onClose}
      />
    ),
    failure: <TxFailure operation="claim-lowers" onClose={onClose} message={errorMsg} />,
  };

  if (!transaction) {
    return <></>;
  }

  return (
    <Dialog.Root open={isOpen}>
      <Dialog.Portal>
        <Dialog.Overlay>
          <Dialog.Content forceMount>{stepComponents[step]}</Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
}
